import { useEffect, useMemo } from 'react'
import type { AdvancedTableProps, ArrayResponse, Order } from './AdvancedTable.types'
import Table from '@mui/material/Table'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import { Box, IconButton } from '@mui/material'
import RequestStatus from 'components/RequestStatus'
import * as Styled from './AdvancedTable.styles'
import { useAppContext } from 'context/AppContext/AppContext'
// import TableNav from './TableNav'
import useQueryGet from 'hooks/useQueryGet'
// import { renderDate } from 'utils/renderDate'
import FiltersProvider, { useFilters } from 'context/FiltersContext/FiltersContext'
import { renderDate } from 'utils/renderDate'
import { SortIcon } from 'assets/icons/sort-button'
import { SortDesc } from 'assets/icons/sort-desc'
import { SortAsc } from 'assets/icons/sort-asc'
import { Pagination } from './partials/pagination'
import { showPerPageName } from 'config/globalVariables'

const AdvancedTable: <T extends object>(props: AdvancedTableProps<T>) => JSX.Element = ({
  headings,
  name,
  endpoint,
  renderRow,
  defaultRowsPerPage,
  formHandler,
  formId,
  orderField,
  orderDir,
  type,
}) => {
  const { filters, updateFilter, setResultsCount } = useFilters()
  const { isDarkMode } = useAppContext()

  const defaultTake = defaultRowsPerPage || localStorage.getItem(showPerPageName) || 100

  useEffect(() => {
    updateFilter({
      // take: defaultRowsPerPage,
      page: '1',
      order: orderDir ?? 'ASC',
      orderField: orderField ?? ((headings.find(e => e.field && !!e.sort)?.field as string) || 'id'),
    })
  }, [])

  const { data, isLoading, isRefetching } = useQueryGet<
    ArrayResponse<NonNullable<typeof type>> | Array<NonNullable<typeof type>>
  >({
    queryKey: [`${name}-query`, filters],
    endpoint,
    options: {
      params: {
        ...Object.fromEntries(
          Object.entries(filters).filter(([fieldKey, fieldValue]) => !!fieldValue && !endpoint.includes(fieldKey))
        ),
        take: defaultTake,
        createdAtStart: filters.createdAtStart
          ? renderDate(filters.createdAtStart.toString(), true, '-', true, false)
          : undefined,
        createdAtFinish: filters.createdAtFinish
          ? renderDate(filters.createdAtFinish.toString(), true, '-', true, false)
          : undefined,
        createdAtLocalStart: filters.createdAtLocalStart
          ? renderDate(filters.createdAtLocalStart.toString(), true, '-', true, false)
          : undefined,
        createdAtLocalFinish: filters.createdAtLocalFinish
          ? renderDate(filters.createdAtLocalFinish.toString(), true, '-', true, false)
          : undefined,
        verifiedAtLocalStart: filters.verifiedAtLocalStart
          ? renderDate(filters.verifiedAtLocalStart.toString(), true, '-', true, false)
          : undefined,
        verifiedAtLocalFinish: filters.verifiedAtLocalFinish
          ? renderDate(filters.verifiedAtLocalFinish.toString(), true, '-', true, false)
          : undefined,
      },
    },
    refetchDeps: [filters],
  })

  const content = useMemo(() => {
    if (
      data === undefined ||
      'error' in data ||
      ('data' in data && data.data instanceof Array && data.data.length === 0)
    ) {
      setResultsCount(null)
      return (
        <TableRow sx={{ '&:hover': { backgroundColor: 'background.default' } }}>
          <TableCell colSpan={headings.length} sx={{ backgroundColor: 'transparent' }}>
            <RequestStatus data={data} isLoading={isLoading} />
          </TableCell>
        </TableRow>
      )
    }

    if (Array.isArray(data)) {
      setResultsCount(data.length)

      return data.map(renderRow)
    }

    setResultsCount(data.meta.itemCount)

    return data.data.map(renderRow)
  }, [data, isLoading])

  const handleChangeOrder = (orderField: string): void => {
    const getDirection = (name: string, dir: Order['dir']): Order['dir'] => {
      if (name === orderField) {
        return dir === 'ASC' ? 'DESC' : 'ASC'
      }
      return 'ASC'
    }
    updateFilter({ order: getDirection(filters.orderField?.toString(), filters.order as Order['dir']), orderField })
  }

  if (data === undefined || 'error' in data || isRefetching) {
    return <RequestStatus data={data} isLoading={isLoading || isRefetching} />
  }

  return (
    <Box>
      <form id={formId} onSubmit={formHandler}>
        <TableContainer component={Paper} sx={{ overflowX: 'auto', background: 'transparent', boxShadow: 'none' }}>
          <Table
            sx={{ borderSpacing: '0px', borderCollapse: 'separate', backgroundColor: 'background.paper' }}
            aria-label="custom pagination table"
          >
            <Styled.TableHead>
              <TableRow>
                {headings.map(({ name, sort, field, width, hide }) =>
                  // eslint-disable-next-line
                  hide === undefined || (hide !== undefined && !hide) ? (
                    <TableCell
                      key={`${field}-${name}`}
                      width={width}
                      sx={{ py: 0.25, width: `${width}px`, minWidth: `${width}px` }}
                    >
                      <Box
                        display="flex"
                        alignItems="center"
                        sx={{ whiteSpace: 'nowrap', color: 'grey.500', fontWeight: 600, fontSize: 12 }}
                      >
                        {name}
                        {sort !== undefined && sort ? (
                          <IconButton
                            color={filters.orderField === field ? 'success' : 'default'}
                            onClick={() => {
                              handleChangeOrder(field)
                            }}
                            size="small"
                          >
                            {filters.orderField !== field ? (
                              <SortIcon />
                            ) : filters.order === 'ASC' ? (
                              <SortDesc />
                            ) : (
                              <SortAsc />
                            )}
                          </IconButton>
                        ) : null}
                      </Box>
                    </TableCell>
                  ) : null
                )}
              </TableRow>
            </Styled.TableHead>
            <Styled.TableBody isdarkmode={isDarkMode.toString()}>{content}</Styled.TableBody>
          </Table>
        </TableContainer>
      </form>
      {'meta' in data && data.meta.itemCount > Number(defaultTake) ? (
        <Pagination
          take={+defaultTake}
          page={+filters.page}
          colSpan={headings.length}
          total={data.meta.itemCount}
          changePage={num => {
            updateFilter({ page: num.toString() })
          }}
        />
      ) : null}
    </Box>
  )
}

const AdvancedTableWrapper: <T extends object>(props: AdvancedTableProps<T>) => JSX.Element = props => {
  return (
    <FiltersProvider
      key={props.name}
      options={props.options}
      headings={props.headings}
      customFilters={props.customFilters ?? []}
      isExportMode={props.isExportMode ?? false}
      exportText={props.exportText}
      disableFilters={props.disableFilters}
      onUsedFilters={props.onUsedFilters}
      onFormChange={props.onFormChange}
      onExport={props.onExport}
      orderField={props.orderField}
      changeExportMode={props.changeExportMode}
      orderDir={props.orderDir}
      renderFilters={props.renderFilters}
      columnsToExport={props.columnsToExport}
      filterGroups={props.filterGroups}
      disableSearchParams={props.disableSearchParams}
    >
      <AdvancedTable {...props} />
    </FiltersProvider>
  )
}

export default AdvancedTableWrapper
